import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

interface DocumentPreviewProps {
  dealId: string;
  documentType: string;
}

export function DocumentPreview({ dealId, documentType }: DocumentPreviewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dealData, setDealData] = useState<any>(null);

  useEffect(() => {
    loadDealData();
  }, [dealId]);

  const loadDealData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from("deals")
        .select("*")
        .eq("id", dealId)
        .single();

      if (error) throw error;
      setDealData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load deal data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!dealData) {
    return (
      <Alert>
        <AlertDescription>No deal data found</AlertDescription>
      </Alert>
    );
  }

  const renderDocument = () => {
    const currentDate = new Date().toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    switch (documentType) {
      case "coverLetter":
        return (
          <div className="space-y-6 p-8 bg-background border rounded-lg">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-primary">CONTRACT FUNDING GROUP, LLC</h1>
              <p className="text-lg font-semibold text-accent">Making real estate investments simple</p>
            </div>
            
            <div className="space-y-4 mt-8">
              <p>{currentDate}</p>
              <p className="font-semibold">{dealData.seller_name}</p>
              <p>{dealData.seller_address}</p>
              
              <p className="mt-6">Dear {dealData.seller_name},</p>
              
              <p>
                Thank you for considering Contract Funding Group for the purchase of your land contract at{" "}
                {dealData.property_address}. We are pleased to present our offer for your review.
              </p>
              
              <div className="my-6 p-4 bg-muted rounded-lg">
                <h3 className="font-semibold mb-2">Offer Summary:</h3>
                <p><strong>Property:</strong> {dealData.property_address}</p>
                <p><strong>Offer Price:</strong> ${dealData.offer_price?.toLocaleString()}</p>
                <p><strong>Current Balance:</strong> ${dealData.current_balance?.toLocaleString()}</p>
                <p><strong>Monthly Payment:</strong> ${dealData.payment_amount?.toLocaleString()}</p>
              </div>
              
              <p>
                Enclosed you will find the necessary documents for your review and signature. Please review
                all documents carefully and return them at your earliest convenience.
              </p>
              
              <p>We look forward to working with you.</p>
              
              <div className="mt-8">
                <p>Sincerely,</p>
                <div className="mt-12 border-t border-foreground w-48" />
                <p className="font-semibold">Contract Funding Group Team</p>
              </div>
            </div>
          </div>
        );

      case "agreement":
        return (
          <div className="space-y-4 p-8 bg-background border rounded-lg">
            <h2 className="text-xl font-bold text-center">PURCHASE AGREEMENT</h2>
            <p className="text-center text-sm text-muted-foreground">
              For Land Contract at {dealData.property_address}
            </p>
            
            <div className="space-y-4 mt-6">
              <p><strong>Date:</strong> {currentDate}</p>
              <p><strong>Seller:</strong> {dealData.seller_name}</p>
              <p><strong>Buyer:</strong> {dealData.buyer_name}</p>
              <p><strong>Property:</strong> {dealData.property_address}</p>
              
              <div className="my-4 p-4 bg-muted rounded-lg space-y-2">
                <p><strong>Purchase Price:</strong> ${dealData.offer_price?.toLocaleString()}</p>
                <p><strong>Original Sales Price:</strong> ${dealData.sales_price?.toLocaleString()}</p>
                <p><strong>Current Balance:</strong> ${dealData.current_balance?.toLocaleString()}</p>
                <p><strong>Interest Rate:</strong> {dealData.interest_rate}%</p>
                <p><strong>Monthly Payment:</strong> ${dealData.payment_amount?.toLocaleString()}</p>
              </div>
              
              <p className="text-sm">
                [Full agreement terms and conditions would appear here in the actual document]
              </p>
            </div>
          </div>
        );

      case "verification":
        return (
          <div className="space-y-4 p-8 bg-background border rounded-lg">
            <h2 className="text-xl font-bold text-center">CONTRACT VERIFICATION FORM</h2>
            
            <div className="space-y-4 mt-6">
              <div>
                <Label>Buyer Name:</Label>
                <p className="ml-4">{dealData.buyer_name}</p>
              </div>
              
              <div>
                <Label>Buyer Address:</Label>
                <p className="ml-4">{dealData.buyer_address}</p>
              </div>
              
              <div>
                <Label>Property Address:</Label>
                <p className="ml-4">{dealData.property_address}</p>
              </div>
              
              <div className="mt-6">
                <p className="font-semibold">Please verify the following:</p>
                <div className="ml-4 mt-2 space-y-2">
                  <p>☐ I am the current buyer on this land contract</p>
                  <p>☐ All payment information is accurate</p>
                  <p>☐ I have possession of the original contract</p>
                </div>
              </div>
              
              <div className="mt-8 space-y-4">
                <div>
                  <p>Signature: _________________________________</p>
                </div>
                <div>
                  <p>Date: _________________________________</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "respa":
        return (
          <div className="space-y-4 p-8 bg-background border rounded-lg">
            <h2 className="text-xl font-bold text-center">RESPA COMPLIANCE CERTIFICATION</h2>
            
            <div className="space-y-4 mt-6">
              <p><strong>Year:</strong> {new Date().getFullYear()}</p>
              <p><strong>Property:</strong> {dealData.property_address}</p>
              
              <div className="mt-6 space-y-3">
                <p className="font-semibold">Seller Certification:</p>
                <div className="ml-4 space-y-2">
                  <p>☐ I did not originate this loan</p>
                  <p>☐ I have originated ____ notes this year</p>
                  <p>☐ Of those, ____ were at high interest rates (&gt;8%)</p>
                </div>
              </div>
              
              <div className="mt-8">
                <p>
                  I certify that the information provided above is true and accurate to the best of my knowledge.
                </p>
              </div>
              
              <div className="mt-8 space-y-4">
                <div>
                  <p>Signature: _________________________________</p>
                </div>
                <div>
                  <p>Date: _________________________________</p>
                </div>
              </div>
            </div>
          </div>
        );

      case "checklist":
        return (
          <div className="space-y-4 p-8 bg-background border rounded-lg">
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold text-primary">CONTRACT FUNDING GROUP, LLC</h1>
              <p className="text-lg font-semibold text-accent">Making real estate investments simple</p>
            </div>
            
            <div className="space-y-4 mt-8">
              <p>{currentDate}</p>
              <p className="font-semibold">{dealData.seller_name}</p>
              
              <p className="mt-6">Dear {dealData.seller_name},</p>
              
              <p>
                Below is a checklist of items needed for your cash offer underwriting:
              </p>
              
              <div className="my-6 space-y-2">
                <p className="font-semibold">Required Documentation:</p>
                <div className="ml-4 space-y-2">
                  <p>☐ Signed Purchase Agreement</p>
                  <p>☐ Proof of last 6 months of payments from buyer</p>
                  <p>☐ Copy of original land contract</p>
                  <p>☐ Completed Contract Verification Form</p>
                  <p>☐ Completed RESPA Compliance Form</p>
                  <p>☐ Current property tax statement</p>
                </div>
              </div>
              
              <p>
                Please review this checklist carefully and gather all required documents.
              </p>
              
              <div className="mt-8">
                <p>Sincerely,</p>
                <div className="mt-12 border-t border-foreground w-48" />
                <p className="font-semibold">Contract Funding Group Team</p>
              </div>
            </div>
          </div>
        );

      default:
        return <p>Unknown document type</p>;
    }
  };

  return (
    <div className="document-preview">
      {renderDocument()}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <span className="font-semibold">{children}</span>;
}
